/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, string, func, shape,
} from 'prop-types';
import { useDispatch } from 'react-redux';

import ComponentErrorBoundary from '../Common/ComponentErrorBoundary/ComponentErrorBoundary';
import SimpleCarouselBuilder from '../Common/SimpleCarouselBuilder';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import noop from '../../../../helpers/noop';
import { emitProductImpression, emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import BuyItAgainRecsGridUI from './BuyItAgainRecsGridUI/BuyItAgainRecsGridUI';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const BuyItAgainRecsBuilder = ({
    data, blockObj, setShowSkeleton,
}) => {
    const dispatch = useDispatch();

    const {
        products_to_show,
        min_products_to_show,
        buy_again_title,
        css_classes,
        title_margin,
        title_font_color,
        title_font_size,
        title_font_family,
        title_font_style,
        align_title = 'center',
        padding,
    } = blockObj || {};

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: data?.buyAgainRecs,
        isFireImpression: data?.buyAgainRecs?.campaign?.campaignResponses?.[0]?.experienceName === 'Control',
        page: { type: 'home' },
    });

    useEffect(() => {
        setShowSkeleton(false);

        const hasProducts = (data?.buyAgainRecs?.products?.length > 0)
            || (data?.buyAgainRecs?.productRecs?.length > 0);

        if (!hasProducts) return;

        const displayType = data?.buyAgainRecs?.campaign?.campaignResponses?.[0]?.payload?.displayType || '';
        const products = data?.buyAgainRecs?.products;

        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            salesforceResponse: data?.buyAgainRecs,
            page: { type: 'home' },
            eventSubType: 'campaign-tracking',
            displayType,
            min_products_to_show,
        }));

        if ((displayType === 'BOTH' || displayType === 'CUST_PRODUCTS_ONLY') && products?.length >= min_products_to_show) {
            dispatch(emitProductImpression({
                action: 'Impression',
                productsPayload: {
                    products,
                    mobileBreakpoint: 600,
                },
                salesforceResponse: data?.buyAgainRecs,
                page: { type: 'home' },
            }));
        }
    }, []);

    const handleClick = (partNumber, isFromProductRecs) => {
        const gaTagging = data?.buyAgainRecs?.productsContent?.ga_tagging || {};
        const gaTagging1 = data?.buyAgainRecs?.productRecsContent?.ga_tagging || {};
        const trackingData = isFromProductRecs ? gaTagging1 : gaTagging;

        trackClickThrough(partNumber);
        dispatch(trackEvent({
            eventCategory: trackingData?.event_category || 'Product',
            eventAction: trackingData?.event_action || 'Click',
            eventLabel: `${trackingData?.event_label} - ${partNumber}` || 'Buy It Again',
            eventName: 'feature_interaction',
            feature_category: trackingData?.event_category || 'Product',
            feature_element: trackingData?.event_action || 'Click',
            feature_detail: `${trackingData?.event_label} - ${partNumber}` || 'Buy It Again',
        }));
    };

    const renderCarousel = (carouselTitle, products, isFromProductRecs) => (
        <ComponentErrorBoundary function="BuyItAgainRecsSFContainer" module="PersonalizedContent" message="Something went wrong when displaying Buy It Again Recommendations from Salesforce">
            <SimpleCarouselBuilder
                blockData={{
                    productsToShow: products_to_show,
                    title: carouselTitle,
                    css_classes,
                    titleMargin: title_margin,
                    titleFontColor: title_font_color?.color,
                    titleFontSize: title_font_size,
                    titleFontStyle: title_font_style,
                    titleFontFamily: title_font_family,
                    alignTitle: align_title,
                    padding,
                }}
                productClickCallback={(partNumber) => handleClick(partNumber, isFromProductRecs)}
                products={products}
                demandType="XS"
            />
        </ComponentErrorBoundary>
    );

    const renderBuyAgainGridUI = (salesforceDataResponse, cmsBlockObj) => (
        <ComponentErrorBoundary function="BuyItAgainRecsGridUI" module="PersonalizedContent" message="Something went wrong when displaying Recently Viewed Recommendations from Salesforce">
            <BuyItAgainRecsGridUI sfData={salesforceDataResponse} cmsData={cmsBlockObj} isNewDesign={false} handleClickCallback={handleClick} />
        </ComponentErrorBoundary>
    );

    if ((data?.buyAgainRecs?.products && data?.buyAgainRecs?.products.length > 0)
        || (data?.buyAgainRecs?.productRecs && data?.buyAgainRecs?.productRecs.length > 0)
    ) {
        const titleSF = data?.buyAgainRecs?.campaign?.campaignResponses?.[0]?.payload?.productRecTitle || data?.buyAgainRecs?.campaign?.campaignResponses?.[0]?.payload?.fallbackProductRecTitle;
        const displayType = data?.buyAgainRecs?.campaign?.campaignResponses?.[0]?.payload?.displayType || '';
        const products = data?.buyAgainRecs?.products;
        const productRecs = data?.buyAgainRecs?.productRecs;
        const isGridView = data?.buyAgainRecs?.campaign?.campaignResponses?.[0]?.experienceName?.includes('GRID');

        switch (displayType) {
            case 'BOTH':
                if (isGridView) return renderBuyAgainGridUI(data, blockObj);

                return (
                    <>
                        {renderCarousel(titleSF, productRecs, true)}
                        {renderCarousel(buy_again_title || 'Buy It Again', products?.length >= min_products_to_show ? products : [], false)}
                    </>
                );

            case 'CUST_PRODUCTS_ONLY':
                if (isGridView) return renderBuyAgainGridUI(data, blockObj);

                return renderCarousel(buy_again_title || 'Buy It Again', products?.length >= min_products_to_show ? products : [], false);

            case 'PRODUCT_RECS_ONLY':
                if (isGridView) return renderBuyAgainGridUI(data, blockObj);
                return renderCarousel(titleSF, productRecs, true);

            default:
                return null;
        }
    }
    return null;
};

BuyItAgainRecsBuilder.propTypes = {
    data: object.isRequired,
    blockObj: shape({
        title_font_color: shape({
            color: string.isRequired,
        }),
        title_font_size: string,
        title_font_style: string,
        title_font_family: string,
        padding: string,
    }).isRequired,
    setShowSkeleton: func,
};

BuyItAgainRecsBuilder.defaultProps = {
    setShowSkeleton: noop,
};

export default BuyItAgainRecsBuilder;
